import {
    HotelMealKind,
    HotelReviewViewModel,
    ReviewAmenityGroup,
    ReviewGalleryCategory,
    ReviewRateOccupancy,
    ReviewRateOption,
    ReviewRoomGroup,
    ReviewSearchInfo,
    ReviewStayOccupancy,
    RoomContent,
    RoomTypeContent,
    RoomVariance,
    SelectHotelData
} from '@/src/types/hotels/details'
import { sumCsv } from './index'
import { localized } from './localized'

const getMealKind = (rawMealEn: string, mealCode: string): HotelMealKind => {
    const meal = (rawMealEn || '').toLowerCase()
    if (mealCode === 'RO' || !meal || meal === 'none' || meal === 'room only') return 'roomOnly'
    if (mealCode === 'BB' || meal === 'breakfast') return 'breakfast'
    return 'other'
}

const getBedType = (variance: RoomVariance, locale: string): string => {
    const room = variance.room[0]
    const raw = localized(room?.lbt, locale) || room?.bt || ''
    return raw
        .split(',')
        .map(part => part.trim())
        .filter(part => part && part.toLowerCase() !== 'null')
        .join(', ')
}

// User-currency `up.*` is the display source of truth; fall back to converted p/wdp
// (never native np/swdp) so the amount and its currency label stay consistent.
const getRatePrice = (variance: RoomVariance) => {
    const currency = variance.up?.uc || variance.p?.cur || ''
    const upNow = Number(variance.up?.p)
    const upWas = Number(variance.up?.wdp)
    const now = upNow > 0 ? upNow : variance.p?.val || 0
    const was = upWas > 0 ? upWas : variance.wdp?.val || 0
    const hasDiscount = was > 0 && was !== now
    return {
        currency,
        now,
        was,
        hasDiscount,
        discountPct: hasDiscount ? Math.round(((was - now) / was) * 100) : 0
    }
}

// A deal is a genuine price reduction only. `defRm` (default room) and `pChange`
// (price changed since search) are unrelated flags and must not badge as deals.
const isHotDeal = (variance: RoomVariance): boolean => variance.wdp?.val > 0 && variance.wdp.val !== variance.p?.val

/**
 * Guests aged 17 or under count as children to Cleartrip, matching the threshold the
 * Flyin adapter already applies when it builds its own occupancy payloads.
 */
const CHILD_MAX_AGE = 17

const toRateOccupancy = (variance: RoomVariance): ReviewRateOccupancy[] =>
    variance.room.map(room => ({
        adults: Number(room.ocp?.na) || 0,
        childAges: (room.ocp?.pax ?? [])
            .map(pax => Number(pax.age))
            .filter(age => Number.isFinite(age) && age <= CHILD_MAX_AGE)
    }))

/**
 * `searchInfo` echoes the criteria back per room: `nAd`/`nCh` are per-room CSVs ("2,1"),
 * and `chAgs` groups the ages by room with "|" — so "|0" means no children in room 1 and
 * one aged 0 in room 2.
 */
const toStayOccupancy = (searchInfo?: ReviewSearchInfo | null): ReviewStayOccupancy[] => {
    const roomCount = Number(searchInfo?.nRms) || 1
    const adultsPerRoom = (searchInfo?.nAd ?? '').split(',')
    const childrenPerRoom = (searchInfo?.nCh ?? '').split(',')
    const agesPerRoom = (searchInfo?.chAgs ?? '').split('|')

    return Array.from({ length: roomCount }, (_, index) => ({
        adults: Number(adultsPerRoom[index]) || 1,
        children: Number(childrenPerRoom[index]) || 0,
        // Age 0 is a real value Cleartrip sends, so empty strings are dropped before Number()
        // rather than filtering falsy results afterwards.
        childAges: (agesPerRoom[index] ?? '')
            .split(',')
            .filter(part => part.trim() !== '')
            .map(part => Number(part))
            .filter(age => Number.isFinite(age))
    }))
}

const toRateOption = (variance: RoomVariance, locale: string, nights: number): ReviewRateOption => {
    const room = variance.room[0]
    const rawMealEn = room?.lmn?.en || room?.mn || ''
    return {
        rId: variance.rId ?? '0',
        arNo: variance.index ?? '0',
        booking: {
            hotelId: variance.ht?.ctuid ?? '',
            bookingCode: room?.pi?.bookingCode ?? '',
            roomTypeCode: room?.rcd ?? '',
            checkIn: variance.ad ?? '',
            nights: Number(variance.dur) || nights,
            occupancy: toRateOccupancy(variance)
        },
        mealKind: getMealKind(rawMealEn, room?.mcd || ''),
        mealName: localized(room?.lmn, locale) || room?.mn || '',
        inclusions: (room?.pi?.inclusions || '')
            .split(',')
            .map(item => item.trim())
            .filter(Boolean),
        price: getRatePrice(variance),
        nights,
        rooms: variance.room.length || 1,
        isHotDeal: isHotDeal(variance)
    }
}

const cleanHtml = (value: string | null | undefined): string =>
    (value || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

// Entity-decode + tag-strip for parsed about-text (mirrors the RN HotelInfoTab cleaner).
const decodeHtmlEntities = (value: string): string =>
    value
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&#39;/g, "'")
        .replace(/&quot;/g, '"')
        .replace(/&nbsp;/g, ' ')

const cleanAboutText = (html: string): string =>
    decodeHtmlEntities(html.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, ' '))
        .replace(/\s+/g, ' ')
        .trim()

// Parse "<p><b>Label:</b> body</p>" blocks into { label, body } sections (mirrors the RN
// HotelInfoTab parser) so the overview renders as structured paragraphs, not one blob.
const parseAboutSections = (html: string | null | undefined): { label: string; body: string }[] => {
    const source = html || ''
    const sections: { label: string; body: string }[] = []
    const paragraphRegex = /<p[^>]*>([\s\S]*?)<\/p>/gi
    let match: RegExpExecArray | null
    while ((match = paragraphRegex.exec(source)) !== null) {
        const inner = match[1]
        const boldMatch = inner.match(/<b[^>]*>([\s\S]*?)<\/b>/i)
        let label = ''
        let body = inner
        if (boldMatch) {
            label = cleanAboutText(boldMatch[1]).replace(/:?\s*$/, '')
            body = inner.replace(/<b[^>]*>[\s\S]*?<\/b>/i, '')
        }
        body = cleanAboutText(body)
        if (!label && !body) continue
        sections.push({ label, body })
    }
    if (sections.length === 0) {
        const body = cleanAboutText(source)
        if (body) sections.push({ label: '', body })
    }
    return sections
}

const recordToLines = (record: Record<string, string> | undefined): string[] =>
    record ? Object.values(record).filter(Boolean) : []

// Version-gated roomContent — render only when the contract version matches.
const getRoomContent = (data: SelectHotelData): RoomContent | undefined =>
    data.roomContent?.v === 1 ? data.roomContent : undefined

// Per-room-type content resolved by the group's first room index (ri), keyed as a string.
const getGroupRoomContent = (
    group: RoomVariance[],
    roomContent: RoomContent | undefined
): RoomTypeContent | undefined => {
    const firstRi = group[0]?.room[0]?.ri
    if (!roomContent || firstRi == null) return undefined
    return roomContent.rooms[String(firstRi)]
}

const toAmenityGroups = (content: RoomTypeContent | undefined, locale: string): ReviewAmenityGroup[] =>
    (content?.amen || [])
        .map(group => ({
            title: localized(group.cat, locale),
            items: group.list.map(item => localized(item, locale)).filter(Boolean)
        }))
        .filter(group => group.items.length > 0)

const toGalleryCategories = (content: RoomContent | undefined, locale: string): ReviewGalleryCategory[] =>
    (content?.hotel.gallery || [])
        .map(section => ({
            title: localized(section.cat, locale),
            images: (section.img || []).filter(Boolean)
        }))
        .filter(section => section.images.length > 0)

// selectHotel → ready-to-render view model. `up.*` prices are already converted to `uc`.
export const transformHotelReview = (data: SelectHotelData, locale: string): HotelReviewViewModel | null => {
    const { hotelInfo, searchInfo } = data
    if (!hotelInfo?.basicInfo) return null

    const { basicInfo } = hotelInfo
    const nights = Number(searchInfo?.nights) || 1
    const roomContent = getRoomContent(data)

    const groupList = Array.isArray(data.roomVarianceList) ? data.roomVarianceList : []
    const roomGroups: ReviewRoomGroup[] = groupList
        .filter(group => Array.isArray(group) && group.length > 0)
        .map(group => {
            const first = group[0]
            const room = first.room[0]
            const content = getGroupRoomContent(group, roomContent)
            return {
                roomName: localized(room?.lrn, locale) || room?.rn || '',
                bedType: getBedType(first, locale),
                occupancyAdults: Number(room?.ocp?.na) || 1,
                options: group.map(variance => toRateOption(variance, locale, nights)),
                roomImages: content?.img?.filter(Boolean) || [],
                roomSize: content?.rs ?? null,
                viewType: content?.vt ? localized(content.vt, locale) : '',
                amenityGroups: toAmenityGroups(content, locale)
            }
        })

    const images = [hotelInfo.images?.main, ...(hotelInfo.images?.others || [])].filter(Boolean) as string[]

    const attractions = (hotelInfo.attractions || []).slice(0, 5).map(item => `${item.landmarkName} — ${item.distance}`)

    return {
        hotel: {
            name: basicInfo.name,
            address: basicInfo.addressText,
            starCount: Math.round(Number(basicInfo.starRating)) || 0,
            images:
                images.length > 0
                    ? images
                    : [basicInfo.mainImage].filter(Boolean),
            amenities: {
                parking: hotelInfo.parkingAvail,
                wifi: hotelInfo.wifiAvail,
                swim: hotelInfo.swimAvail,
                gym: hotelInfo.gymAvail,
                roomService: hotelInfo.roomSerAvail,
                bar: hotelInfo.barAvail
            },
            checkInTime: roomContent?.hotel.checkIn || hotelInfo.policy?.check_in || '',
            checkOutTime: roomContent?.hotel.checkOut || hotelInfo.policy?.check_out || '',
            geo: basicInfo.geoCode,
            cityName: basicInfo.city?.name || '',
            stateName: basicInfo.province?.name || '',
            countryCode: basicInfo.country?.countryCode || '',
            tags: (roomContent?.hotel.tags || []).map(tag => localized(tag, locale)).filter(Boolean),
            numOfRooms: roomContent?.hotel.numOfRooms ?? null,
            numOfFloors: roomContent?.hotel.numOfFloors ?? null,
            galleryCategories: toGalleryCategories(roomContent, locale)
        },
        stay: {
            checkIn: data.cin,
            checkOut: data.cout,
            nights,
            rooms: searchInfo?.nRms || 1,
            adults: sumCsv(searchInfo?.nAd || '0'),
            children: sumCsv(searchInfo?.nCh || '0'),
            occupancy: toStayOccupancy(searchInfo)
        },
        roomGroups,
        info: {
            about: parseAboutSections(hotelInfo.descriptions?.aboutHotel),
            location: cleanHtml(hotelInfo.descriptions?.location),
            facilities: {
                general: recordToLines(hotelInfo.facilities?.general),
                services: recordToLines(hotelInfo.facilities?.services),
                activities: recordToLines(hotelInfo.facilities?.activities)
            },
            policies: {
                checkIn:
                    roomContent?.hotel.checkIn || hotelInfo.ctPolicies?.checkIn || hotelInfo.policy?.check_in || '',
                checkOut:
                    roomContent?.hotel.checkOut || hotelInfo.ctPolicies?.checkOut || hotelInfo.policy?.check_out || '',
                cancellation: cleanHtml(hotelInfo.policy?.cancellationPolicy),
                child: cleanHtml(hotelInfo.policy?.childPolicy),
                pet: cleanHtml(hotelInfo.policy?.petPolicy),
                mandatoryFees: cleanHtml(hotelInfo.ctPolicies?.mandatoryFees)
            },
            attractions
        },
        productId: data.productId,
        echoToken: data.uuid,
        uid: basicInfo.uniqueId,
        currency: data.currency || ''
    }
}
